import { useMemo } from 'react';
import {
  buildTargetReadinessItems,
  type TargetReadinessItem,
} from '../../components/dashboard/target-readiness';
import { useApplicationById } from '../api/useApplicationById';
import { useApplicationBehavior } from './useApplicationBehavior';
import { useAuthenticationInfo } from './useAuthenticationInfo';
import { useHomeScanningStatus } from './useHomeScanningStatus';
import { useSourceCodeInfo } from './useSourceCodeInfo';
import { useTestingControls } from './useTestingControls';

export type TargetProfileFacts = {
  url: string | null;
  environment: string | null;
  techStack: string[];
  authMechanisms: string[];
  hasWaf: boolean | null;
};

export function useTargetProfile(applicationId: string) {
  const { application, isLoading: isApplicationLoading } = useApplicationById({
    id: applicationId,
  });
  const { data: behavior, isLoading: isBehaviorLoading } =
    useApplicationBehavior(applicationId);
  const { data: authentication, isLoading: isAuthenticationLoading } =
    useAuthenticationInfo(applicationId);
  const { data: sourceCode, isLoading: isSourceCodeLoading } =
    useSourceCodeInfo(applicationId);
  const testingControls = useTestingControls(applicationId);
  const { status: scanning } = useHomeScanningStatus(applicationId);

  const isLoading =
    isApplicationLoading ||
    isBehaviorLoading ||
    isAuthenticationLoading ||
    isSourceCodeLoading ||
    testingControls.isLoading;

  return useMemo((): {
    isLoading: boolean;
    facts: TargetProfileFacts | null;
    readiness: TargetReadinessItem[];
  } => {
    if (!application) {
      return { isLoading, facts: null, readiness: [] };
    }

    return {
      isLoading,
      facts: {
        url: application.url ?? null,
        environment: application.environment ?? null,
        techStack: behavior?.infrastructure.techStack ?? [],
        authMechanisms: behavior?.authentication.mechanisms ?? [],
        hasWaf: application.hasWaf ?? null,
      },
      readiness: buildTargetReadinessItems({
        credentials: {
          total: authentication?.credentials.total ?? 0,
          verified: authentication?.credentials.verified ?? 0,
          failed: authentication?.credentials.failed ?? 0,
        },
        repositoryCount: sourceCode?.repositoryCount ?? 0,
        isSourceCodeConnected: sourceCode?.isConnected ?? false,
        domainCount: application.domains.length,
        signalCategoryCount: application.signalCategoriesInScope?.length ?? 0,
        isInternalApp: application.isInternalApp ?? false,
        hasInternalAccessNetwork: Boolean(application.internalAccessNetworkId),
        scanning: scanning ?? {
          kind: 'automation',
          enabled: application.smokeTestEnabled ?? false,
        },
        instructionCount: testingControls.instructionCount,
        customGuardrailCount: testingControls.customGuardrailCount,
      }),
    };
  }, [
    application,
    authentication,
    behavior,
    isLoading,
    scanning,
    sourceCode,
    testingControls.customGuardrailCount,
    testingControls.instructionCount,
  ]);
}
